/**
 * @file usePreviousItemRemoval.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description The Item picker's X on "Recently ordered" / "Recently bought"
 *              (Cam, 30 Oct 2026: "so users can remove items from that list,
 *              and items are removed for everyone, and only join the list again
 *              if/when the removed item is ordered again following its
 *              removal").
 *
 *              One hook for the four screens that feed the picker — Create /
 *              Edit Customer Order and Create Invoice (side 'customer'), Create
 *              / Edit PO and Add / Edit Bill (side 'supplier'). Each already
 *              holds the list it fetched (`previousItems`); this:
 *                - takes the row off that list at once (every line's picker
 *                  reads the same list), then asks the server;
 *                - refused or failed: puts the row back where it was and says
 *                  why;
 *                - done: "Removed P12193 Supreme Rearer 16 from D J Smith’s
 *                  recently ordered — Undo"; Undo asks the server to forget the
 *                  removal and puts the row back.
 *              No confirmation — Undo is the way back. Other screens pick the
 *              change up on their next fetch of the list.
 *
 *              const { removePrevious, toast } = usePreviousItemRemoval({
 *                side: 'customer', accountId, accountName, previousItems, setPreviousItems });
 *              <OrderLinesGrid … onRemovePrevious={removePrevious} />
 *              {toast}
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config';
import UndoToast from '../components/UndoToast';
import { removedMessage, restoredMessage } from '../utils/previousItemRemovals';
import { itemCodeOf, itemNameOf } from '../utils/customerPricing';

/** The endpoint for one account's list. */
export function previousItemsUrl(side, accountId, action) {
  const base = side === 'supplier'
    ? `${API_URL}/api/purchase-orders/supplier-previous-items`
    : `${API_URL}/api/orders/customer-previous-items`;
  return `${base}/${encodeURIComponent(accountId)}/${action}`;
}

async function post(url, itemId) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ inventory_item_id: itemId }),
  });
  let body = {};
  try { body = await res.json(); } catch (_) { /* no body */ }
  if (!res.ok || body.success === false) {
    throw new Error(body.error || `The server said no (${res.status})`);
  }
  return body;
}

export default function usePreviousItemRemoval({ side, accountId, accountName, previousItems, setPreviousItems }) {
  const [toast, setToast] = useState(null);
  // The account and list as they are NOW — an answer arriving after the
  // screen moved to another customer must not touch the new one's list.
  const accountRef = useRef(accountId);
  const listRef = useRef(previousItems);
  const nameRef = useRef(accountName);
  useEffect(() => { accountRef.current = accountId; }, [accountId]);
  useEffect(() => { listRef.current = previousItems; }, [previousItems]);
  useEffect(() => { nameRef.current = accountName; }, [accountName]);

  const putBack = useCallback((row, index) => {
    setPreviousItems((list) => {
      const current = Array.isArray(list) ? list : [];
      if (current.some(p => p.inventory_item_id === row.inventory_item_id)) return current;
      const next = [...current];
      next.splice(Math.max(0, Math.min(index, next.length)), 0, row);
      return next;
    });
  }, [setPreviousItems]);

  const removePrevious = useCallback(async (row, item) => {
    const acc = accountRef.current;
    if (!acc || !row || row.inventory_item_id == null) return;
    const list = Array.isArray(listRef.current) ? listRef.current : [];
    const index = list.findIndex(p => p.inventory_item_id === row.inventory_item_id);
    const original = index >= 0 ? list[index] : row;
    const words = { side, code: itemCodeOf(item), name: itemNameOf(item), accountName: nameRef.current };

    setPreviousItems((l) => (Array.isArray(l) ? l.filter(p => p.inventory_item_id !== row.inventory_item_id) : l));
    try {
      await post(previousItemsUrl(side, acc, 'remove'), row.inventory_item_id);
    } catch (err) {
      if (accountRef.current === acc) putBack(original, index);
      setToast({ tone: 'error', message: `${itemCodeOf(item) || 'The item'} is still on the list — ${err.message}`, at: Date.now() });
      return;
    }

    const undo = async () => {
      try {
        await post(previousItemsUrl(side, acc, 'restore'), row.inventory_item_id);
        if (accountRef.current === acc) putBack(original, index);
        setToast({ tone: 'info', message: restoredMessage(words), at: Date.now() });
      } catch (err) {
        setToast({ tone: 'error', message: `Couldn’t put ${itemCodeOf(item) || 'the item'} back — ${err.message}`, at: Date.now() });
      }
    };
    setToast({ tone: 'info', message: removedMessage(words), actionLabel: 'Undo', onAction: undo, at: Date.now() });
  }, [side, setPreviousItems, putBack]);

  const element = toast
    ? <UndoToast toast={toast} onDismiss={() => setToast(null)} />
    : null;

  return { removePrevious, toast: element };
}
